<!--
PlatformWebhooksForm — the fields of one subscription of the platform events
console (/config/webhooks, plano §14). Pure form layer: v-model of a
PlatformWebhooks_SubscriptionSaveInput, no network — PlatformWebhooksModal owns
the save. `validateForm()` is exposed to the modal and, like every form of the
system, surfaces its own failure (form-valid=false + globalMessage.error).

The subscription is keyed by plugin name on the backend (upsert), so in edit
mode the name is read-only: changing it would CREATE a second subscription
instead of updating this one.
-->
<template>
  <div class="platform-webhooks-form" data-testid="platform-webhooks-form">
    <input-text
      elem-id="platformWebhooksFormPlugin"
      :model-value="modelValue.plugin"
      :label="t('Nome (plugin ou serviço)')"
      :placeholder="'leads-manager'"
      :disabled="isEdit || readOnly"
      :ng-required="true"
      data-testid="platform-webhooks-form-plugin"
      @update:model-value="patch({ plugin: String($event ?? '') })"
    />
    <select-list
      elem-id="platformWebhooksFormDelivery"
      :disabled="readOnly"
      :model-value="modelValue.delivery"
      :label="t('Entrega')"
      :itens-select="deliveryOptions"
      :auto-select-first="false"
      data-testid="platform-webhooks-form-delivery"
      @update:model-value="patch({ delivery: String($event ?? 'webhook') })"
    />
    <input-text
      v-if="modelValue.delivery !== 'nats'"
      elem-id="platformWebhooksFormUrl"
      :disabled="readOnly"
      :model-value="modelValue.url"
      :label="t('URL (https://…)')"
      :placeholder="'https://leads-manager.newshoes.com/api/platform/events'"
      :ng-required="true"
      data-testid="platform-webhooks-form-url"
      @update:model-value="patch({ url: String($event ?? '') })"
    />
    <p v-else class="platform-webhooks-form-hint" data-testid="platform-webhooks-form-nats-hint">
      {{ t('Entrega por NATS: o serviço puxa do consumidor plugin-<nome> dentro do compose; não há URL nem segredo HMAC.') }}
    </p>
    <select-list-multiple
      elem-id="platformWebhooksFormEvents"
      :disabled="readOnly"
      :model-value="modelValue.events"
      :label="t('Eventos')"
      :itens-select="eventPatternOptions"
      data-testid="platform-webhooks-form-events"
      @update:model-value="patch({ events: ($event as string[]) ?? [] })"
    />
    <input-text
      v-if="modelValue.delivery !== 'nats'"
      elem-id="platformWebhooksFormSecret"
      :disabled="readOnly"
      :model-value="modelValue.secret"
      :label="isEdit ? t('Segredo (vazio = manter o atual)') : t('Segredo (vazio = cunhar)')"
      data-testid="platform-webhooks-form-secret"
      @update:model-value="patch({ secret: String($event ?? '') })"
    />
    <select-list
      elem-id="platformWebhooksFormContract"
      :disabled="readOnly"
      :model-value="modelValue.contract"
      :label="t('Envelope')"
      :itens-select="contractOptions"
      :auto-select-first="false"
      data-testid="platform-webhooks-form-contract"
      @update:model-value="patch({ contract: String($event ?? '2.0') })"
    />
    <input-toggle
      elem-id="platformWebhooksFormEnabled"
      :disabled="readOnly"
      :model-value="modelValue.enabled"
      :label="t('Ativa')"
      data-testid="platform-webhooks-form-enabled"
      @update:model-value="patch({ enabled: Boolean($event) })"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '@/eventBus'
import type { PlatformWebhooks_SubscriptionSaveInput } from '@/contracts/PlatformWebhooks_SubscriptionSaveInput'

const props = defineProps<{
  modelValue: PlatformWebhooks_SubscriptionSaveInput
  /** catalog event names — the options are the names plus one `<entity>.*` prefix per entity */
  eventNames: string[]
  isEdit?: boolean
  /** assinatura gerida pela stack: os campos vêm da declaração do serviço e não se editam aqui */
  readOnly?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: PlatformWebhooks_SubscriptionSaveInput]
  'form-valid': [valid: boolean]
}>()

const { t } = useI18n()

const contractOptions = [
  { ID: '2.0', Name: '2.0 (CloudEvents)' },
  { ID: '1.1', Name: '1.1 (legado)' },
]
const deliveryOptions = computed(() => [
  { ID: 'webhook', Name: t('webhook (POST assinado)') },
  { ID: 'nats', Name: t('nats (serviço no compose)') },
])
const eventPatternOptions = computed(() => {
  const names = props.eventNames.filter((n) => n !== '')
  const prefixes = Array.from(new Set(names.map((n) => `${n.split('.')[0]}.*`)))
  return [...prefixes.map((p) => ({ ID: p, Name: p })), ...names.map((n) => ({ ID: n, Name: n }))]
})

function patch(part: Partial<PlatformWebhooks_SubscriptionSaveInput>): void {
  emit('update:modelValue', { ...props.modelValue, ...part })
}

/** Submit gate — the modal calls it before saving. Surfaces the failure itself. */
function validateForm(): boolean {
  const v = props.modelValue
  let reason = ''
  // Every field of the generated Input contract is optional, so an unset one is
  // undefined here — exactly the state this gate exists to reject. Defaulting to
  // empty makes "missing" and "blank" fail the same way, which is what the
  // messages already say.
  if ((v.plugin ?? '').trim() === '') reason = t('Informe o nome do plugin ou serviço')
  else if ((v.events ?? []).length === 0) reason = t('Escolha ao menos um evento')
  else if (v.delivery !== 'nats' && !/^https?:\/\//.test((v.url ?? '').trim())) reason = t('Informe a URL (https://…) que recebe o POST assinado')
  if (reason !== '') {
    emit('form-valid', false)
    eventBus.emit('form.validation.failed', { form: 'platform-webhooks', reason })
    eventBus.emit('globalMessage.error', reason)
    return false
  }
  emit('form-valid', true)
  return true
}

defineExpose({ validateForm })
</script>

<style scoped>
.platform-webhooks-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.platform-webhooks-form-hint {
  margin: -0.5rem 0 0 0;
  font-size: 0.8rem;
  color: var(--p-text-muted-color, #6b7280);
}
</style>
